import { encodeContextValue } from "./context"
import { stringifyExact } from "./json"
import { type Ux, type UxDataBinding, type UxPage, byId } from "./model"
import { message, NoticeError, rendererNotice, type Notice } from "./messages"

/** Why a binding cannot be read here, with the backend issue that would make it possible. */
export interface Unavailable {
  readonly reason: string
  readonly notice?: Notice
  readonly issue?: number
}

/** How to ask the cell for one binding (`GET /v1/widgets/{widgetId}/bindings/{bindingId}`), or why not. */
export type BindingRequest =
  | { readonly kind: "ready"; readonly query: Readonly<Record<string, string>> }
  | { readonly kind: "awaiting_selection"; readonly sourceBindingId: string }
  | { readonly kind: "unavailable"; readonly why: Unavailable }

/** The cell bounds dependency expansion at 64 bindings; so does the shell. */
const DEPENDENCY_BOUND = 64

/** Bindings a query reads from: a traversal's source and any `record` filter or key value. */
export function directDependencies(binding: UxDataBinding): string[] {
  const q = binding.query
  const values = q.kind === "object_list" ? q.filters.map((f) => f.value) : q.kind === "object_detail" ? q.key.map((k) => k.value) : []
  const fromValues = values.flatMap((v) => (v.kind === "record" ? [v.property.bindingId] : []))
  const all = [...(q.kind === "link_traversal" ? [q.sourceBindingId] : []), ...fromValues]
  return [...new Set(all)].filter((id) => id !== binding.bindingId)
}

/** Pages whose context a query reads. */
function contextPages(binding: UxDataBinding): string[] {
  const q = binding.query
  if (q.kind === "object_runtime_detail") return [q.pageId]
  const values = q.kind === "object_list" ? q.filters.map((f) => f.value) : q.kind === "object_detail" ? q.key.map((k) => k.value) : []
  return values.flatMap((v) => (v.kind === "context_parameter" ? [v.pageId] : []))
}

/** A JSON Pointer token (`~` → `~0`, `/` → `~1`), as the endpoint's `context/<page>/<param>` names require. */
export const pointerToken = (s: string) => s.replace(/~/g, "~0").replace(/\//g, "~1")
export const contextParamName = (pageId: string, paramId: string) => `context/${pointerToken(pageId)}/${pointerToken(paramId)}`

/**
 * Build the request for one binding. The cell executes filters, sorting, paging and traversal; the
 * shell supplies only what the contract asks for:
 * - typed context for every page the binding or its transitive dependencies read, page-qualified;
 * - `record.<sourceBindingId>` for each list or traversal dependency, from that list's single current
 *   selection. Detail dependencies resolve on the cell from their declared keys.
 * Nothing is guessed: a missing selection waits for one, other pages' context is unavailable here.
 */
export function bindingRequest(
  binding: UxDataBinding,
  all: ReadonlyMap<string, UxDataBinding>,
  page: UxPage,
  context: Readonly<Record<string, unknown>>,
  currentKey: (bindingId: string) => string | undefined,
  locale = "en",
): BindingRequest {
  const deps: UxDataBinding[] = []
  const queue = directDependencies(binding)
  const seen = new Set<string>([binding.bindingId])
  while (queue.length > 0) {
    const id = queue.shift() as string
    if (seen.has(id)) continue
    seen.add(id)
    const dep = all.get(id)
    if (!dep) return { kind: "unavailable", why: unavailable({ key: "binding.undeclaredDependency", values: { bindingId: id } }, locale) }
    if (seen.size > DEPENDENCY_BOUND) return { kind: "unavailable", why: unavailable({ key: "binding.dependencyLimit", values: { count: DEPENDENCY_BOUND } }, locale) }
    deps.push(dep)
    queue.push(...directDependencies(dep))
  }
  const query: Record<string, string> = {}
  for (const dep of deps) {
    if (dep.query.kind === "object_detail" || dep.query.kind === "object_runtime_detail") continue
    const key = currentKey(dep.bindingId)
    if (key === undefined) return { kind: "awaiting_selection", sourceBindingId: dep.bindingId }
    query[`record.${dep.bindingId}`] = key
  }
  const pages = new Set([binding, ...deps].flatMap(contextPages))
  for (const pageId of pages) {
    if (pageId !== page.pageId) return { kind: "unavailable", why: unavailable({ key: "binding.otherPageContext", values: { pageId } }, locale) }
    for (const param of page.contextParams) {
      // JSON parameters travel as exact JSON text; scalars as their exact text.
      const value = !Object.hasOwn(context, param.paramId) ? null : param.type === "json" ? stringifyExact(context[param.paramId]) : encodeContextValue(context[param.paramId])
      if (value !== null) query[contextParamName(pageId, param.paramId)] = value
      else if (param.required) return { kind: "unavailable", why: unavailable({ key: "binding.requiredParameter", values: { paramId: param.paramId } }, locale) }
    }
  }
  return { kind: "ready", query }
}

function unavailable(notice: Notice, locale: string): Unavailable {
  return { reason: rendererNotice(notice, locale), notice }
}

/** The endpoint query string for a ready request, with an optional continuation cursor. */
export function bindingQueryString(query: Readonly<Record<string, string>>, cursor?: string | null): string {
  const params = new URLSearchParams(Object.entries(query))
  if (cursor) params.set("cursor", cursor)
  return params.toString()
}

/** The query string for one binding of an app, or an error saying why it cannot be read yet. */
export function bindingQuery(bindingId: string, ux: Ux, pageId: string, context: Readonly<Record<string, unknown>>, recordKeys: ReadonlyMap<string, string>, cursor?: string, locale = "en"): string {
  const all = byId(ux.dataBindings, (b) => b.bindingId)
  const binding = all.get(bindingId)
  const page = ux.pages.find((p) => p.pageId === pageId)
  if (!binding || !page) throw new NoticeError({ key: "binding.queryUndeclared", values: { bindingId, pageId } }, (key, values) => message("en", key, values))
  const req = bindingRequest(binding, all, page, context, (id) => recordKeys.get(id), locale)
  if (req.kind === "awaiting_selection") throw new NoticeError({ key: "binding.queryAwaitingSelection", values: { bindingId, sourceBindingId: req.sourceBindingId } }, (key, values) => message("en", key, values))
  if (req.kind === "unavailable") throw new NoticeError(req.why.notice ?? { text: req.why.reason }, (key, values) => message("en", key, values))
  return bindingQueryString(req.query, cursor)
}
